import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Lines } from "../site/Reveal";
import { Btn } from "../site/Btn";
import { useLang } from "../../i18n/lang";
import { useSiteImages } from "../../lib/siteImages";

const EASE = [0.76, 0, 0.24, 1];

export const Hero = () => {
  const { t, to } = useLang();
  const img = useSiteImages();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden bg-[#1A1A1A] text-[#FFFFFF]" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y, scale }}>
        {img("hero") && <motion.img src={img("hero")} alt="Sculptural travertine table in a contemporary architectural interior with natural side light"
          fetchpriority="high" className="w-full h-full object-cover object-[50%_60%]"
          initial={{ clipPath: "inset(14% 22% 14% 22%)", scale: 1.25 }} animate={{ clipPath: "inset(0% 0% 0% 0%)", scale: 1 }}
          transition={{ duration: 2, ease: EASE, delay: 0.15 }} />}
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#121212]/80 via-[#121212]/25 to-[#121212]/20" />
      <div className="grain absolute inset-0" />

      <motion.div style={{ y: textY, opacity: fade }} className="relative h-full flex flex-col justify-end px-6 sm:px-10 lg:px-14 pb-12 sm:pb-16">
        <motion.p className="eyebrow text-[#E5E5E5] mb-8 flex items-center gap-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 1 }}>
          <span>THE STONE EDITION</span><span className="w-10 h-px bg-[#E5E5E5]" /><span className="hidden sm:inline">{t.hero.label}</span>
        </motion.p>
        <Lines as="h1" onMount delay={1} testid="hero-title" lines={[t.hero.l1, <em key="e" className="italic text-[#E0E0E0]">{t.hero.l2}</em>]}
          className="f-serif font-light tracking-[-0.025em] leading-[0.95] text-[3.1rem] sm:text-7xl lg:text-[7.6rem]" />
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <motion.p className="lg:col-span-5 text-base sm:text-lg text-[#EBEBEB] leading-relaxed max-w-md" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.6, duration: 1 }}>
            “{t.hero.quote}”
          </motion.p>
          <motion.div className="lg:col-span-7 flex flex-col sm:flex-row gap-3 lg:justify-end" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.8, duration: 1 }}>
            <Btn to={to("/stones")} variant="light" testid="hero-explore-stones">{t.hero.cta1}</Btn>
            <Btn to={to("/start-a-project")} variant="ghostLight" testid="hero-commission">{t.hero.cta2}</Btn>
          </motion.div>
        </div>
      </motion.div>

      <motion.div className="hidden md:flex absolute right-10 lg:right-14 top-1/2 -translate-y-1/2 flex-col items-center gap-4 eyebrow text-[#E5E5E5]"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2, duration: 1 }}>
        <span className="[writing-mode:vertical-rl] rotate-180">{t.common.scroll}</span>
        <span className="relative w-px h-16 bg-[#FFFFFF]/25 overflow-hidden">
          <motion.span className="absolute inset-x-0 top-0 h-6 bg-[#FFFFFF]" animate={{ y: [-24, 64] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }} />
        </span>
      </motion.div>
    </section>
  );
};
